/**
 * Mobile-only bottom-sheet wrapper for ``SampleBallotView``. Renders as
 * an inline ``position: fixed`` element (no portal — see the
 * ``BallotSheetProvider`` rationale comment for why).
 *
 * Layout: the sheet's geometry interpolates between a "closed rect"
 * and a "full rect" by ``heightFrac``. Both rects are computed below.
 *
 *   - When ``chatInputRect`` is null (landing page / new chat), the
 *     closed rect is the teal header band at the bottom of the
 *     viewport, full viewport width. The full rect is the entire
 *     ``viewport - headerOffset`` area.
 *   - When ``chatInputRect`` is set (active chat), the closed rect is
 *     the teal header band sitting flush on top of the chat input,
 *     matching its width and horizontal position. The full rect is
 *     still the entire ``viewport - headerOffset`` area — so dragging
 *     up grows the sheet vertically *and* horizontally until it
 *     covers the input.
 *
 * Position is driven by interpolating CSS ``top`` / ``bottom`` /
 * ``left`` / ``right`` rather than ``translateY``, because we need to
 * animate width too.
 *
 * Gesture model: a single native ``touchstart`` / ``touchmove`` /
 * ``touchend`` listener attached imperatively (via ``useEffect``) on
 * the sheet root handles every touch inside the sheet. On each
 * touchmove it walks up from the target to find a scrollable
 * ancestor:
 *
 *   - No scrollable ancestor (touch landed on the header, address bar,
 *     or tabs): if vertical movement passes the drag threshold, take
 *     over and resize the sheet 1:1 with the finger.
 *   - Scrollable ancestor at its top boundary and finger moving down,
 *     OR at its bottom boundary and finger moving up: hand off — let
 *     the native scroll finish at the boundary, then start resizing
 *     the sheet from the finger's current position.
 *   - Otherwise: do nothing and let native scroll handle the gesture.
 *
 * ``touchmove`` is registered ``{passive: false}`` so we can
 * ``preventDefault`` to suppress native scroll / rubber-band during
 * the handoff window.
 *
 * Why a native listener instead of React props on the JSX: React's
 * synthetic touchmove is always passive, so ``preventDefault`` is a
 * no-op there. Native ``addEventListener`` is the only way to opt
 * into ``{passive: false}``.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import LocationOnIcon from '@mui/icons-material/LocationOn';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Box, IconButton, Typography } from '@mui/material';

import { useAuth } from '@/components/auth/AuthProvider';
import BallotStatusButton from '@/components/ballot/BallotStatusButton';
import SampleBallotView from '@/components/ballot/SampleBallotView';
import useAnalytics from '@/hooks/useAnalytics';
import useGuestAddress from '@/hooks/useGuestAddress';
import useGuestBallotDownload from '@/hooks/useGuestBallotDownload';
import { useBallotSheet, useBallotSheetLive } from '@/providers/BallotSheetProvider';
import { primitiveColors, semanticColors, BORDER_RADIUS_LG } from '@/theme/tokens';
import BallotIcon from '@/ui-library/BallotIcon';

// Vertical height of the teal header band — the only part of the
// sheet visible when closed. Used in BOTH the visual layout and the
// touch-listener drag math. Matches the active-chat ``BallotGrabber``
// so the crossfade between the two on drag is visually seamless.
// Kept in sync with ``BallotGrabber.HEADER_HEIGHT_PX`` and the
// provider's local copy used in safe-area math. Sized to fit the
// title + locality stack (~32px content) with breathing room for
// the pill and vertical padding.
const HEADER_HEIGHT_PX = 56;

// ``heightFrac`` at which width / bottom-anchor have fully expanded
// to the full-detent values (full viewport width, sheet bottom at
// viewport bottom). Small value = snappy: the sheet jumps to full
// width almost immediately on the first bit of drag, then just the
// top edge keeps climbing toward the app header. From this point on
// the only thing animating is the top edge.
const WIDTH_GROWTH_COMPLETE_FRAC = 0.15;

// Min vertical movement before we treat a touch as a sheet drag.
// 10px clears the browser's tap-slop, so a tab/button below the
// touch target still receives its click if the user wasn't actually
// trying to drag.
const HEADER_DRAG_THRESHOLD_PX = 10;
// Direction filter for the header gate: only commit to drag when the
// gesture is at least this much more vertical than horizontal.
// Matches the mobile app's ``captureGrabberPanResponder`` ratio.
const VERTICAL_DOMINANCE_RATIO = 1.5;
// Slack (px) for "at top" and "at bottom" — iOS sometimes reports
// scrollTop slightly off zero or off the max even when visually at
// the boundary.
const SCROLL_BOUNDARY_SLACK_PX = 1;

interface BallotSheetProps {
  /** Forwarded to ``SampleBallotView`` so the sheet renders the right
   * data source (personal Cosmos / national snapshot). When omitted,
   * the value falls back to ``ballotMode`` on the BallotSheetProvider
   * context (set by ``DashboardLayout`` from auth state). */
  mode?: 'personal' | 'national';
}

type GestureMode = 'none' | 'pending-scroll' | 'pending-header' | 'dragging';

function findScrollAncestor(start: Element | null, stopAt: Element): HTMLElement | null {
  let el = start;
  while (el && el !== stopAt) {
    if (el instanceof HTMLElement) {
      const overflowY = getComputedStyle(el).overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
        return el;
      }
    }
    el = el.parentElement;
  }
  return null;
}

const BallotSheet: React.FC<BallotSheetProps> = ({ mode }) => {
  const { t } = useTranslation('common');
  const { t: tBallot } = useTranslation('ballot');
  const { isAuthenticated } = useAuth();
  const {
    detent,
    ballotMode,
    locality,
    isDragging,
    viewportHeightPx,
    headerOffsetPx,
    chatInputRect,
    keyboardInsetPx,
    closedDetentFrac,
    safeAreaBottomPx,
    pendingInstantSnap,
    updateDragFrac,
    settleSheet,
    open,
    close,
    snapTo,
    isStatusPopoverActive,
  } = useBallotSheet();
  const { heightFrac } = useBallotSheetLive();

  const effectiveMode = mode ?? ballotMode;
  const { track } = useAnalytics();
  const { address: guestAddress } = useGuestAddress();
  // Show the status control for a real ballot: personal (signed-in) or a guest
  // with a saved address. Hidden only for the pure national snapshot.
  const showStatus = effectiveMode === 'personal' || Boolean(guestAddress?.formatted_address);
  // Download ballot PDF — the mobile-web counterpart of the desktop BallotMenu
  // icon and the mobile app's header button. The export endpoint renders the
  // signed-in account's ballot, so for a guest with a ballot it opens sign in.
  const showDownload = effectiveMode === 'personal' && isAuthenticated;
  const guestDownload = useGuestBallotDownload();
  const downloadRef = useRef<(() => void) | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const sheetRef = useRef<HTMLDivElement>(null);

  // ---- Geometry ----
  // Where the sheet's TOP edge lives at heightFrac = 0 (closed) and
  // heightFrac = 1 (full). At full it's always the app-header bottom.
  // At closed it's the active header height above the anchor (chat
  // input top, or viewport bottom).
  //
  // For the landing case (``!chatInputRect``) we subtract
  // ``safeAreaBottomPx`` so the sheet's box is the FULL header
  // height (52px) above the SafeAreaFiller's top edge. Without this
  // the closed-state box becomes ``HEADER - safeArea`` tall (e.g.,
  // 18px on a 34px-safe-area phone) because we shifted the sheet's
  // CSS bottom up by ``env(safe-area-inset-bottom)`` to clear the
  // filler. Anchoring closed against the chat input (active chat)
  // doesn't have this issue because the input is already positioned
  // above the safe area via its own padding-bottom.
  const closedTopY = chatInputRect
    ? chatInputRect.top - HEADER_HEIGHT_PX
    : viewportHeightPx - HEADER_HEIGHT_PX - safeAreaBottomPx;
  const fullTopY = headerOffsetPx;

  // Where the sheet's BOTTOM edge lives at each end. Expressed as
  // "distance from layout-viewport bottom" so it matches CSS
  // ``bottom``.
  //
  // The landing-state branch (``!chatInputRect``) adds
  // ``keyboardInsetPx`` so the sheet stops at the top of the
  // keyboard rather than extending behind it. CSS ``bottom`` is
  // measured against the LAYOUT viewport (full screen) while
  // ``closedTopY`` is measured against the VISUAL viewport (above
  // the keyboard) — without the inset the sheet box would span from
  // above the keyboard all the way to the layout bottom, leaving
  // the teal header rendering at keyboard-height + 52px instead of
  // just 52px.
  const closedBottomOffset = chatInputRect ? viewportHeightPx - chatInputRect.top : keyboardInsetPx;
  const fullBottomOffset = 0;

  // Horizontal offsets. At closed they match the chat input's
  // horizontal extent (so the teal header reads as a continuation of
  // the input). At full the sheet spans the viewport.
  const closedLeftOffset = chatInputRect ? chatInputRect.left : 0;
  const closedRightOffset = chatInputRect
    ? Math.max(0, document.documentElement.clientWidth - chatInputRect.left - chatInputRect.width)
    : 0;

  // Vertical interpolation uses raw ``heightFrac`` (linear across the
  // whole 0..1 range — the top edge keeps climbing past mid until full).
  // Horizontal + bottom-anchor use ``fastFrac`` which clamps to 1 at
  // ``WIDTH_GROWTH_COMPLETE_FRAC`` (the mid detent), so the sheet
  // reaches full width and viewport-bottom anchor by the time the
  // user has dragged to mid. From mid → full only the top edge moves.
  const fastFrac = Math.min(1, heightFrac / WIDTH_GROWTH_COMPLETE_FRAC);
  const lerp = (closed: number, full: number, frac: number) => closed + (full - closed) * frac;

  // Round to whole pixels so the sheet never sits at a fractional
  // y-position. The internal boundary between the teal header band
  // and the SampleBallotView body below it inherits the sheet's
  // sub-pixel offset; at fractional positions the GPU anti-aliases
  // that boundary into a hairline of the body bg (most visible at
  // the ``mid`` detent, since ``closed`` and ``full`` already land on
  // whole-pixel endpoints). Whole-pixel positioning eliminates the
  // boundary anti-aliasing entirely.
  const topPx = Math.round(lerp(closedTopY, fullTopY, heightFrac));
  const bottomPx = Math.round(lerp(closedBottomOffset, fullBottomOffset, fastFrac));
  const leftPx = Math.round(lerp(closedLeftOffset, 0, fastFrac));
  const rightPx = Math.round(lerp(closedRightOffset, 0, fastFrac));

  // Total finger-travel distance for the sheet to go from closed to
  // full. Drives the drag math so a 1px finger move = 1px of vertical
  // sheet growth.
  const travelPx = Math.max(0, closedTopY - fullTopY);

  // Refs for the imperative touch handler so the listener doesn't need
  // to re-attach on every drag-induced re-render.
  const detentRef = useRef(detent);
  const travelPxRef = useRef(travelPx);
  // ``closed`` is dynamic — it sits at ``PREVIEW_CLOSED_FRAC`` when
  // ``previewClosed`` is on (guest landing, no address). Mirror it
  // into a ref so the touch listener reads the current value at
  // gesture time without having to re-attach the listener whenever
  // ``previewClosed`` flips.
  const closedDetentFracRef = useRef(closedDetentFrac);
  // Ref-route ``updateDragFrac`` + ``settleSheet`` too. ``settleSheet``
  // is a ``useCallback`` whose deps include ``detentFracs``, which
  // re-memoizes when ``previewTargetHeightPx`` arrives from
  // ``SampleBallotView``'s post-mount measurement. Pre-ref, that
  // re-memoization tore down and re-attached the touch listener — if
  // the measurement landed mid-drag (the common case for the first
  // gesture after a refresh on the new chat page with an address),
  // the in-flight gesture's closure was replaced and the drag froze
  // with ``dragFrac`` stuck at its last value. Reading through refs
  // means the listener effect only depends on the (always-stable)
  // sheet element ref.
  const updateDragFracRef = useRef(updateDragFrac);
  const settleSheetRef = useRef(settleSheet);
  const isStatusPopoverActiveRef = useRef(isStatusPopoverActive);
  // Captured at touchstart: was the status popover up when this tap began? Its
  // dismiss tap must not open the sheet even on Safari's delayed synthetic click.
  const headerOpenSuppressedRef = useRef(false);
  useEffect(() => {
    detentRef.current = detent;
    travelPxRef.current = travelPx;
    closedDetentFracRef.current = closedDetentFrac;
    updateDragFracRef.current = updateDragFrac;
    settleSheetRef.current = settleSheet;
    isStatusPopoverActiveRef.current = isStatusPopoverActive;
  }, [detent, travelPx, closedDetentFrac, updateDragFrac, settleSheet, isStatusPopoverActive]);

  useEffect(() => {
    const sheetEl = sheetRef.current;
    if (!sheetEl) return undefined;

    let gestureMode: GestureMode = 'none';
    let startX = 0;
    let startY = 0;
    let startFrac = 0;
    let startDetent: typeof detent = 'closed';
    let lastY = 0;
    let lastTime = 0;
    let velocityPxPerMs = 0;
    // Drag was handed off from the bottom (an expand) — may only raise, not lower.
    let dragFromBottom = false;

    const detentFracs: Record<typeof detent, number> = {
      // ``closed``'s actual frac is read from the ref each gesture
      // so previewClosed-driven rest positions work; mid/full are
      // constants from ``BallotSheetProvider.DETENT_FRACS_BASE``.
      get closed() {
        return closedDetentFracRef.current;
      },
      mid: 0.5,
      full: 1,
    };

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) {
        gestureMode = 'none';
        return;
      }
      const t = e.touches[0];
      startX = t.clientX;
      startY = t.clientY;
      lastY = t.clientY;
      lastTime = performance.now();
      velocityPxPerMs = 0;
      startDetent = detentRef.current;
      startFrac = detentFracs[startDetent];
      headerOpenSuppressedRef.current = isStatusPopoverActiveRef.current();

      // Initial classification: did the touch land in a scroll
      // container? If so, we'll only take over when scroll hits a
      // boundary. If not, vertical movement past the threshold is
      // an immediate drag.
      const scrollAncestor = findScrollAncestor(t.target as Element | null, sheetEl);
      gestureMode = scrollAncestor ? 'pending-scroll' : 'pending-header';
      dragFromBottom = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (gestureMode === 'none' || e.touches.length !== 1) return;
      const t = e.touches[0];
      const dy = t.clientY - startY; // +dy = finger moved DOWN
      const dx = t.clientX - startX;

      if (gestureMode === 'pending-header') {
        // Direction-gated commit: clearly vertical and past
        // threshold. Otherwise leave the gesture alone (e.g.,
        // horizontal swipes shouldn't grab the sheet).
        const absDy = Math.abs(dy);
        const absDx = Math.abs(dx);
        if (absDy >= HEADER_DRAG_THRESHOLD_PX && absDy > absDx * VERTICAL_DOMINANCE_RATIO) {
          gestureMode = 'dragging';
        }
      } else if (gestureMode === 'pending-scroll') {
        // Wait until scroll is pinned at a boundary AND the finger
        // is still moving past it.
        const scrollEl = findScrollAncestor(t.target as Element | null, sheetEl);
        if (scrollEl) {
          const atTop = scrollEl.scrollTop <= SCROLL_BOUNDARY_SLACK_PX;
          const atBottom =
            scrollEl.scrollTop + scrollEl.clientHeight >=
            scrollEl.scrollHeight - SCROLL_BOUNDARY_SLACK_PX;
          const movingDown = t.clientY - lastY > 0;
          const movingUp = t.clientY - lastY < 0;
          const enterViaTop = atTop && movingDown; // content at top → allow collapse
          // At bottom → expand, but only when there's room. At full there's
          // nothing to grow, and entering a drag would preventDefault and kill the
          // native scroll, so a following down-swipe would lower the sheet instead
          // of scrolling the content back up.
          const enterViaBottom = atBottom && movingUp && detentRef.current !== 'full';
          if (enterViaTop || enterViaBottom) {
            // Reset the drag baseline to *this* point so the sheet tracks the
            // finger 1:1 from handoff; ``startDetent`` stays the resting detent
            // (what ``settleSheet`` snaps against).
            startY = t.clientY;
            startDetent = detentRef.current;
            startFrac = detentFracs[startDetent];
            gestureMode = 'dragging';
            dragFromBottom = enterViaBottom;
          }
        }
      }

      if (gestureMode === 'dragging') {
        // Suppress native scroll / rubber-band for the rest of this
        // gesture. Requires the listener to be ``{passive: false}``.
        e.preventDefault();
        const travel = travelPxRef.current;
        if (travel > 0) {
          const dragDy = t.clientY - startY;
          const deltaFrac = -dragDy / travel; // up = grow
          // A bottom (expand) handoff may only raise — reversing down stops at the
          // start detent rather than collapsing (content isn't at the top).
          const nextFrac = dragFromBottom
            ? Math.max(startFrac, startFrac + deltaFrac)
            : startFrac + deltaFrac;
          updateDragFracRef.current(nextFrac);
        }
        // Velocity tracking — px/ms, positive = finger moving up.
        const now = performance.now();
        const dt = now - lastTime;
        if (dt > 0) {
          velocityPxPerMs = (lastY - t.clientY) / dt;
        }
        lastY = t.clientY;
        lastTime = now;
      } else {
        // Still tracking last position even if not yet a drag — so
        // the moment we transition into 'dragging' the first frame
        // already has a reasonable lastY/time baseline.
        lastY = t.clientY;
        lastTime = performance.now();
      }
    };

    const handleTouchEnd = () => {
      if (gestureMode === 'dragging') {
        // Pass the gesture's starting detent so ``settleSheet`` can
        // apply its directional 25%-snap and one-detent fling logic.
        const travel = travelPxRef.current;
        const dragDy = lastY - startY;
        const currentFrac = travel > 0 ? Math.max(0, Math.min(1, startFrac + -dragDy / travel)) : 0;
        settleSheetRef.current(currentFrac, velocityPxPerMs, startDetent);
      }
      gestureMode = 'none';
    };

    sheetEl.addEventListener('touchstart', handleTouchStart, { passive: true });
    sheetEl.addEventListener('touchmove', handleTouchMove, { passive: false });
    sheetEl.addEventListener('touchend', handleTouchEnd);
    sheetEl.addEventListener('touchcancel', handleTouchEnd);

    return () => {
      sheetEl.removeEventListener('touchstart', handleTouchStart);
      sheetEl.removeEventListener('touchmove', handleTouchMove);
      sheetEl.removeEventListener('touchend', handleTouchEnd);
      sheetEl.removeEventListener('touchcancel', handleTouchEnd);
    };
    // Empty deps — handlers read from refs above so the listener
    // attaches exactly once for the component's lifetime. No mid-
    // drag teardowns when ``settleSheet`` or ``updateDragFrac``
    // re-memoize.
  }, []);

  // Document-level tap-to-close. The ``BallotBackdrop`` is
  // ``pointer-events: none`` so it doesn't block native chat scroll
  // in the visible-above-sheet area; we recover the tap-to-close
  // affordance here. A real tap fires a ``click`` event on whatever
  // is under the pointer at touchend (skipped if the user moved past
  // the browser's tap-slop, so panning to scroll does NOT trigger
  // this). We close when the click landed outside the sheet's DOM
  // and outside the app bar (so app-bar interactions don't dismiss).
  //
  // Uses ``event.composedPath()`` rather than
  // ``sheetRef.current.contains(e.target)`` because some children
  // (e.g., the Google Places autocomplete dropdown) unmount their
  // clicked element *synchronously* from the click handler. By the
  // time this document listener runs, the original target has been
  // detached from the DOM, so ``contains()`` would return false even
  // though the click originated inside the sheet. ``composedPath()``
  // is captured at event dispatch time and isn't affected by
  // subsequent DOM mutations.
  useEffect(() => {
    if (detent === 'closed') return undefined;
    // Selectors for interactive UI surfaces that should NEVER dismiss
    // the sheet. Without these, opening the left-pane menu, tapping
    // its "My Sample Ballot" item, or clicking the in-page menu
    // trigger fires through to this document listener and closes the
    // sheet — the click bubbles up because it landed outside the
    // sheet's DOM. We keep tap-to-close active for "true outside"
    // clicks (the chat backdrop area, raw text content) but exclude
    // app chrome, overlay containers, and any actionable element.
    const IGNORE_SELECTOR =
      '.MuiAppBar-root, .MuiDrawer-root, .MuiDialog-root, .MuiPopover-root, .MuiModal-root, button, a, [role="button"], [role="menuitem"], [role="link"], input, textarea, select';
    const onDocumentClick = (e: MouseEvent) => {
      const path = e.composedPath();
      const sheetEl = sheetRef.current;
      for (const node of path) {
        if (sheetEl && node === sheetEl) return;
        if (node instanceof Element && node.closest?.(IGNORE_SELECTOR)) return;
      }
      close();
    };
    document.addEventListener('click', onDocumentClick);
    return () => document.removeEventListener('click', onDocumentClick);
  }, [detent, close]);

  // Tap on the header to open from closed. A drag triggers the touch
  // listener instead — that path skips this onClick because (a) the
  // listener calls ``preventDefault`` on touchmove when it commits to
  // a drag, and (b) the browser's ~10px tap-slop would also suppress
  // the synthetic click. Tracking lives here (not on the touch
  // listener) so non-touch interactions (keyboard, mouse) fire it too.
  const handleHeaderClick = useCallback(() => {
    if (detent !== 'closed') return;
    // A tap dismissing the status popover can hit the header — don't open.
    if (headerOpenSuppressedRef.current || isStatusPopoverActive()) {
      headerOpenSuppressedRef.current = false;
      return;
    }
    track('Ballot Sheet Opened', { trigger: 'header' });
    open();
  }, [detent, open, track, isStatusPopoverActive]);

  const handleHeaderKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (detent !== 'closed') return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        track('Ballot Sheet Opened', { trigger: 'header' });
        open();
      }
    },
    [detent, open, track]
  );

  // When a chat input is registered AND the sheet is at the closed
  // resting position, hide this fixed sheet — the in-chat
  // ``BallotGrabber`` (rendered inside the chat scroll container)
  // owns the closed-state visual so it can rubber-band with chat
  // overscroll. As soon as a drag starts (heightFrac > 0) the
  // BallotGrabber fades out and this sheet fades back in to take
  // over the rest of the drag.
  const grabberOwnsClosedState = chatInputRect !== null && heightFrac === 0;

  return (
    <Box
      ref={sheetRef}
      role="dialog"
      aria-modal={detent === 'full'}
      // Shares ``ballotPreviewLabel`` with the inner header button below
      // (and the BallotGrabber it crossfades with). AT announces the
      // dialog then the button with the same name on focus entry — an
      // intentional, pre-existing pattern: every surface that opens this
      // ballot view carries one consistent name. Localized here, not
      // introduced here.
      aria-label={t('ballotPreviewLabel')}
      sx={{
        position: 'fixed',
        // Geometry interpolates between the closed and full rects.
        // At closed (heightFrac=0): sheet sits at chat-input width
        // and position (or viewport bottom in full width if no
        // anchor). At full (heightFrac=1): sheet is full viewport
        // width and reaches headerOffsetPx → viewport bottom.
        top: topPx,
        // ``env(safe-area-inset-bottom)`` lifts the sheet's bottom
        // edge up to the top of the SafeAreaFiller, so the sheet
        // never extends into the home-indicator strip — the filler
        // owns that region with its own context-appropriate color.
        bottom: `calc(${bottomPx}px + env(safe-area-inset-bottom))`,
        left: leftPx,
        right: rightPx,
        zIndex: (theme) => theme.zIndex.modal + 1,
        // ``isDragging`` and ``pendingInstantSnap`` both suppress
        // position transitions — the former while the user's finger
        // is on the sheet, the latter for one render after
        // ``snapTo({ instant: true })``.
        transition:
          isDragging || pendingInstantSnap
            ? 'opacity 100ms ease'
            : 'top 280ms cubic-bezier(0.32, 0.72, 0, 1), bottom 280ms cubic-bezier(0.32, 0.72, 0, 1), left 280ms cubic-bezier(0.32, 0.72, 0, 1), right 280ms cubic-bezier(0.32, 0.72, 0, 1), opacity 100ms ease',
        // ``touch-action: none`` keeps the browser from claiming the
        // touch as a page-level scroll before our drag handlers get
        // a chance to ``preventDefault`` on touchmove.
        touchAction: 'none',
        // See ``grabberOwnsClosedState`` comment above — crossfades
        // with the in-chat BallotGrabber in the active-chat state.
        // Also hide entirely when ``closedDetentFrac > 0`` (the
        // previewClosed state: guest + no address + new chat) —
        // ``MobileAddressBar`` takes over with a simpler, drag-less
        // bar in that scenario, and we don't want the sheet's grabber
        // + label peeking out behind it.
        opacity: grabberOwnsClosedState || closedDetentFrac > 0 ? 0 : 1,
        pointerEvents: grabberOwnsClosedState || closedDetentFrac > 0 ? 'none' : 'auto',
        // Outer bg matches the teal header (rather than the surface
        // white below) so any subpixel sliver of the sheet's box
        // visible past the header's explicit ``HEADER_HEIGHT_PX`` —
        // a rounding artifact in the ``top``/``bottom`` math — reads
        // as the same teal as the header. The ``SampleBallotView``
        // inside paints its own surface bg, so this is only visible
        // at the very edges of the sheet's box.
        backgroundColor: semanticColors.ballotElectionCard,
        // Matches ``BallotGrabber`` (and the composer both dock onto) so the
        // grabber → sheet crossfade keeps one corner shape.
        borderTopLeftRadius: BORDER_RADIUS_LG,
        borderTopRightRadius: BORDER_RADIUS_LG,
        boxShadow: `0 -2px 16px ${semanticColors.shadowLight}`,
        // Mobile-only. On md+ the ballot lives in the right-side
        // BallotMenu / panel; this sheet must not render or it
        // overlays the chat with a teal box on desktop.
        display: { xs: 'flex', md: 'none' },
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header bar — visible above the chat input (or at the
          viewport bottom on landing) when the sheet is closed; the
          sheet's own top edge once dragged open. Drag gestures
          starting here are picked up by the unified touch listener
          on the sheet root. */}
      <Box
        sx={{
          position: 'relative',
          flexShrink: 0,
          height: HEADER_HEIGHT_PX,
          // Match the election-card / accordion-header teal so the
          // closed-state bar reads as part of the same visual family.
          backgroundColor: semanticColors.ballotElectionCard,
          borderTopLeftRadius: BORDER_RADIUS_LG,
          borderTopRightRadius: BORDER_RADIUS_LG,
        }}
      >
        <Box
          onClick={handleHeaderClick}
          onKeyDown={handleHeaderKeyDown}
          role="button"
          tabIndex={0}
          // Same label as the dialog wrapper above (see note there).
          aria-label={t('ballotPreviewLabel')}
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            // Pill anchored to the top of the band; the icon + label
            // wrapper below it expands to fill the remaining height
            // and centers the content vertically within that space.
            justifyContent: 'flex-start',
            height: '100%',
            paddingTop: '6px',
            gap: '4px',
            cursor: detent === 'closed' ? 'pointer' : 'grab',
            '&:active': { cursor: 'grabbing' },
            '&:focus-visible': {
              outline: `2px solid ${primitiveColors.white}`,
              outlineOffset: '-3px',
            },
          }}
        >
          {/* Grabber pill — visual cue that the bar is draggable. The
              actual gesture is handled by the unified touch listener
              on the sheet root, not this element. */}
          <Box
            aria-hidden="true"
            sx={{
              // Pill dimensions match the mobile app's
              // ``BallotBanner`` grabber (32 × 4, 2px radius, 40% white).
              width: 32,
              height: 4,
              borderRadius: '2px',
              backgroundColor: 'rgba(255, 255, 255, 0.4)',
            }}
          />
          {/* Ballot icon + label — matches the mobile app's
              ``BallotBanner`` (icon left of the wordmark on the teal
              band). ``flex: 1`` fills the remaining vertical space
              below the pill; ``alignItems: center`` then vertically
              centers the icon + label within that space, while the
              pill stays pinned at the top. */}
          <Box
            sx={{
              flex: 1,
              alignSelf: 'stretch',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '2px',
              minWidth: 0,
              // Narrow screens: reserve full-width room for the right-pinned status
              // control so a long translated title stays centered and clears it.
              '@media (max-width:420px)': { paddingLeft: '52px', paddingRight: '52px' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                minWidth: 0,
                maxWidth: '100%',
              }}
            >
              <BallotIcon size={16} color={primitiveColors.white} />
              <Typography
                noWrap
                sx={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: 1.2,
                  color: primitiveColors.white,
                  userSelect: 'none',
                  '@media (max-width:420px)': { fontSize: '12px' },
                }}
              >
                {t('ballotPreviewLabel')}
              </Typography>
            </Box>
            {locality && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <LocationOnIcon
                  sx={{ fontSize: 13, color: primitiveColors.white }}
                  aria-hidden="true"
                />
                <Typography
                  sx={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 500,
                    fontSize: '11px',
                    lineHeight: 1.2,
                    color: primitiveColors.white,
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {locality}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Download ballot PDF: pinned to the left of the band, mirroring the
            status control on the right so the title stays centered. Its tap
            triggers the sheet's own SampleBallotView export without toggling
            the sheet (stopPropagation, like BallotStatusButton). */}
        {/* Status pinned left (parity with the reworked mobile sheet header). Its
            tap opens the popover — which now carries the download action — without
            toggling the sheet. Download moves out of the header into that popover. */}
        {showStatus && (
          <Box
            sx={{
              position: 'absolute',
              left: 10,
              top: '14px',
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <BallotStatusButton
              condensed
              onDownload={showDownload ? () => downloadRef.current?.() : guestDownload}
              isDownloading={isDownloading}
            />
          </Box>
        )}
        {/* Close X pinned right — closes the sheet fully. Hidden when already
            closed: a new chat's collapsed sheet still renders this header, and
            an X that closes an already-closed sheet just reads as broken. */}
        {detent !== 'closed' && (
          <Box
            sx={{
              position: 'absolute',
              right: 10,
              top: '14px',
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <IconButton
              size="small"
              aria-label={tBallot('menu.close')}
              onClick={(event) => {
                event.stopPropagation();
                // Collapse the whole sheet instantly — the 280ms height transition
                // left an empty teal band lingering during the close.
                snapTo('closed', { instant: true });
              }}
              onKeyDown={(event) => event.stopPropagation()}
              sx={{
                color: primitiveColors.white,
                '&:hover': { backgroundColor: semanticColors.overlayWhite10 },
              }}
            >
              <XMarkIcon width={20} height={20} />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Sheet content. ``SampleBallotView`` stays mounted at all
          times so the ballot's data (cosmos fetch, scroll position,
          expanded races) survives close/open cycles. When the sheet
          has zero visible height (``heightFrac === 0``), we collapse
          the content area to ``height: 0`` + ``flex: 0 0 0`` so the
          ballot can't peek through the teal header band on browsers
          that render the sheet's box a hair taller than
          ``HEADER_HEIGHT_PX`` (viewport-fit=cover interaction). For
          any visible height — including the ``previewClosed`` rest
          position that lifts the closed detent off zero — the content
          area expands and the ballot is rendered. */}
      <Box
        sx={{
          flex: heightFrac === 0 ? '0 0 0' : 1,
          height: heightFrac === 0 ? 0 : 'auto',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <SampleBallotView
          mode={effectiveMode}
          downloadRef={downloadRef}
          onIsDownloadingChange={setIsDownloading}
        />
      </Box>
    </Box>
  );
};

export default BallotSheet;
